import type { RouteLocationNormalizedLoaded } from 'vue-router';

// For local development: VITE_HELP_CENTER_URL in config.env can point at a help site running
// locally (npm run dev in help-desk). Deployed builds don't pass it, so they use production.
export const HELP_CENTER_URL =
  (import.meta.env.VITE_HELP_CENTER_URL as string | undefined) || 'https://help.factoryfix.com';

/**
 * Help center pages written for each area of the app (help.factoryfix.com/for/<context>).
 * They're defined by CONTEXTS in the help-desk repo's build-tools/config.js, so a slug
 * added or renamed here needs the same change there.
 */
export type HelpContext =
  | 'new'
  | 'manage-jobs'
  | 'job-setup'
  | 'candidates'
  | 'candidate-archive'
  | 'talent-search'
  | 'ai-recruiting-team'
  | 'reporting'
  | 'settings';

// People this new to FactoryFix get the getting-started page wherever they are.
const NEW_USER_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const CONTEXT_BY_ROUTE_NAME: Record<string, HelpContext> = {
  'jobs-list': 'manage-jobs',
  'jobs-copilot-activation': 'job-setup',
  conversations: 'candidates',
  'conversation-tab': 'candidates',
  'applications-tab': 'candidates',
  'resume-tab': 'candidates',
  'notes-tab': 'candidates',
  candidates: 'candidate-archive',
  search: 'talent-search',
  sourcing: 'talent-search',
  'sourcing-index': 'talent-search',
  'sourcing-job-id': 'talent-search',
  'ai-recruiting-team': 'ai-recruiting-team',
  'performance-dashboard': 'reporting',
};

export function helpContextForRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): HelpContext | null {
  const name = typeof route.name === 'string' ? route.name : '';
  // Every Manage Jobs route (the job list, a job's pipeline, an open candidate) is one
  // screen, and the page usually opens with a candidate already selected.
  if (name.startsWith('job-')) return 'manage-jobs';
  if (name.startsWith('settings-')) return 'settings';
  return CONTEXT_BY_ROUTE_NAME[name] ?? null;
}

export function isNewUser(userCreateTs: string | undefined, now: Date = new Date()): boolean {
  const created = userCreateTs ? Date.parse(userCreateTs) : NaN;
  const age = now.getTime() - created;
  // A creation date in the future is bad data, not a new user.
  return Number.isFinite(age) && age >= 0 && age < NEW_USER_DAYS * DAY_MS;
}

/** The help context for where the user is in the app; null means the help center homepage. */
export function helpContextFor(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
  userCreateTs: string | undefined,
  now: Date = new Date(),
): HelpContext | null {
  return isNewUser(userCreateTs, now) ? 'new' : helpContextForRoute(route);
}

export function helpCenterUrl(context: HelpContext | null): string {
  return context ? `${HELP_CENTER_URL}/for/${context}` : HELP_CENTER_URL;
}
